import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { OcpiApiService } from '@api/ocpi/ocpi.api';
import { Constants } from '@utils/Constants';
import { FilterParams } from '../../types/GlobalType';
import { OCPIRole } from '../../types/ocpi/OCPIRole';
import {
  ActionResponse,
  OcpiEndpointDataResult,
  OCPIOperationResultResponse,
  OCPIPingResponse,
  Ordering,
  Paging,
} from '../../types/DataResult';
import { OCPIEndpoint } from '../../types/ocpi/OCPIEndpoint';

@Injectable()
export class OcpiRepositoryService {
  public constructor(public readonly ocpiApi: OcpiApiService) {}

  // COMMON DASHBOARD ROUTES
  public pingOcpiEndpoint(ocpiEndpoint: OCPIEndpoint): Observable<OCPIPingResponse> {
    const alteredOcpiEndpoint = {
      ...ocpiEndpoint,
      id: ocpiEndpoint.id || Constants.NOT_YET_PERSISTED_ID, // The OCPI endpoint must have an ID to be pinged
    };
    return this.ocpiApi.pingOcpiEndpoint(alteredOcpiEndpoint);
  }

  public createOcpiEndpoint(ocpiEndpoint: OCPIEndpoint): Observable<void> {
    return this.ocpiApi.createOcpiEndpoint(ocpiEndpoint);
  }

  public getOcpiEndpoints(
    params: FilterParams = {},
    paging: Paging = Constants.DEFAULT_PAGING,
    ordering: Ordering[] = [],
  ): Observable<OcpiEndpointDataResult> {
    return this.ocpiApi.getOcpiEndpoints(params, paging, ordering);
  }

  public getOcpiToken(endpointName: string, type: OCPIRole): Observable<string> {
    if (type === OCPIRole.CPO) {
      return this.ocpiApi.generateCpoToken(endpointName).pipe(map((response) => response.localToken));
    }
    return this.ocpiApi.generateEmspToken().pipe(map((response) => response.data.token));
  }

  public updateOcpiEndpoint(ocpiEndpoint: OCPIEndpoint): Observable<ActionResponse> {
    return this.ocpiApi.updateOcpiEndpoint(ocpiEndpoint).pipe(
      map(() => ({
        status: 'success',
        message: 'OCPI endpoint updated successfully',
        error: 'An error occurred while updating the OCPI endpoint',
      })),
    );
  }

  public registerOcpiEndpoint(ocpiEndpoint: OCPIEndpoint): Observable<void> {
    return this.ocpiApi.registerEndpoint(ocpiEndpoint.id, ocpiEndpoint.role);
  }

  public unregisterOcpiEndpoint(ocpiEndpoint: OCPIEndpoint): Observable<void> {
    return this.ocpiApi.unregisterOcpiEndpoint(ocpiEndpoint.id, ocpiEndpoint.role);
  }

  public updateOcpiEndpointCredentials(ocpiEndpoint: OCPIEndpoint): Observable<void> {
    return this.ocpiApi.updateEndpointCredentials(ocpiEndpoint.id, ocpiEndpoint.role);
  }

  public pushTokens(ocpiEndpoint: OCPIEndpoint): Observable<void> {
    return this.ocpiApi.pushTokens(ocpiEndpoint.id);
  }

  public deleteOcpiEndpoint(ocpiEndpoint: OCPIEndpoint): Observable<void> {
    return this.ocpiApi.deleteEndpoint(ocpiEndpoint.id);
  }

  // EMSP SIDE
  public fetchLocations(ocpiEndpoint: OCPIEndpoint): Observable<void> {
    return this.ocpiApi.fetchLocations(ocpiEndpoint.id);
  }
  public pullSessionsOcpiEndpoint(ocpiEndpoint: OCPIEndpoint): Observable<OCPIOperationResultResponse> {
    return this.ocpiApi.pullSessionsOcpiEndpoint(ocpiEndpoint.id);
  }
  public pullCdrsOcpiEndpoint(ocpiEndpoint: OCPIEndpoint): Observable<OCPIOperationResultResponse> {
    return this.ocpiApi.pullCdrsOcpiEndpoint(ocpiEndpoint.id);
  }
  public pullTariffsOcpiEndpoint(ocpiEndpoint: OCPIEndpoint): Observable<OCPIOperationResultResponse> {
    return this.ocpiApi.pullTariffsOcpiEndpoint(ocpiEndpoint.id);
  }

  // CPO SIDE
  public sendEVSEStatusesOcpiEndpoint(ocpiEndpoint: OCPIEndpoint): Observable<OCPIOperationResultResponse> {
    return this.ocpiApi.sendEVSEStatusesOcpiEndpoint(ocpiEndpoint.id);
  }
  public pullTokensOcpiEndpoint(ocpiEndpoint: OCPIEndpoint): Observable<OCPIOperationResultResponse> {
    return this.ocpiApi.pullTokensOcpiEndpoint(ocpiEndpoint.id);
  }
  public checkLocationsOcpiEndpoint(ocpiEndpoint: OCPIEndpoint): Observable<OCPIOperationResultResponse> {
    return this.ocpiApi.checkLocationsOcpiEndpoint(ocpiEndpoint.id);
  }
  public checkSessionsOcpiEndpoint(ocpiEndpoint: OCPIEndpoint): Observable<OCPIOperationResultResponse> {
    return this.ocpiApi.checkSessionsOcpiEndpoint(ocpiEndpoint.id);
  }
  public checkCdrsOcpiEndpoint(ocpiEndpoint: OCPIEndpoint): Observable<OCPIOperationResultResponse> {
    return this.ocpiApi.checkCdrsOcpiEndpoint(ocpiEndpoint.id);
  }
  public checkTariffsOcpiEndpoint(ocpiEndpoint: OCPIEndpoint): Observable<OCPIOperationResultResponse> {
    return this.ocpiApi.checkTariffsOcpiEndpoint(ocpiEndpoint.id);
  }
  public pushPeriodDataOcpiEndpoint(ocpiEndpoint: OCPIEndpoint): Observable<OCPIOperationResultResponse> {
    return this.ocpiApi.pushPeriodDataOcpiEndpoint(ocpiEndpoint.id);
  }
}
